import groupBy from 'lodash/groupBy'
import uniqBy from 'lodash/uniqBy'
import { Constants } from '@ciitizen/component-library'
const lodashClonedeep = require('lodash.clonedeep')

/**
 * Find cancer entity details by entity Id
 * @param  {String} entityId     The id of the entity
 * @param  {Object} cancerData   An object with all cancer entities
 * @return {Object}              An object of all the details for a specific cancer entity
 */
export function findCancerEntityById(entityId, cancerData) {
  let foundItem,
    cancerArray = []
  if (cancerData.entityType === 'full_timeline' && cancerData.children) {
    cancerArray = Array.isArray(cancerData.children)
      ? cancerData.children
      : objectToArray(cancerData.children)
  } else {
    cancerArray = Array.isArray(cancerData) ? cancerData : objectToArray(cancerData)
  }
  for (let item of cancerArray) {
    if (item.id === entityId) {
      foundItem = item
      return foundItem
    } else if (item.children) {
      foundItem = findCancerEntityById(entityId, item.children)
    }
    if (foundItem) return foundItem
  }
}

/**
 * Reformats object to be an array
 * @param  {Object} cancerObject  An object with all cancer entities
 * @return {Array}                An array of object reformatted as an array
 */
export function objectToArray(cancerObject) {
  let reformattedData = []
  for (let key in cancerObject) {
    if (key !== 'meta' && key !== 'version' && key !== 'graph') {
      for (let entity of cancerObject[key]) {
        entity.key = key
        reformattedData.push(entity)
      }
    }
  }
  return reformattedData
}

/**
 * Gets details for an entity and all children within it
 * @param  {Array}   item                  An array of cancer entities
 * @param  {Array}   cancerSummary         Cancer summary with graph and detailed entity mappings
 * @return {Array}   final                 An array of detailed items and all subsequent children within it
 */
export function getDetailedEntities(items, cancerSummary) {
  let final = []
  for (let entity of items) {
    let deets = findCancerEntityById(entity.id, cancerSummary)
    if (deets) {
      deets.key = entity.entityType
      entity = Object.assign(entity, deets)
    }
    // get details for all the children of the entity too
    if (entity.children) {
      for (let child of entity.children) {
        let deets = findCancerEntityById(child.id, cancerSummary)
        deets.key = child.entityType
        child = Object.assign(child, deets)
        if (child.children) {
          child.children = getDetailedEntities(child.children, cancerSummary)
        }
      }
    }
    final.push(entity)
  }
  return final
}

export function createTimelineView(graph, allCancerData) {
  let finalTimeline = []
  // go through first layer of graph and exclude treatment groups and group orphan biomarkers by date
  let firstLayerFixes = applyTimelineRules(graph, allCancerData)
  // now go through all primary and secondary diagnosis and apply the same rules
  firstLayerFixes.forEach(item => {
    if (['primaryDiagnosis', 'secondaryDiagnosis'].includes(item.entityType) && item.children) {
      item.children = applyTimelineRules(item.children, allCancerData)
    }
  })
  finalTimeline = finalTimeline.concat(firstLayerFixes)
  return finalTimeline
}

export function applyTimelineRules(cancerGraph, allCancerData) {
  let filteredTimeline = [],
    neuro = allCancerData.find(c => c.meta.diseaseType === 'neuro')
  for (let entity of cancerGraph) {
    /* RULE #1: Show treatment group children but not treatment group itself */
    if (entity.entityType === 'treatmentGroup' && entity.children) {
      // attach treatment group attributes entity for subtext
      let tgEntities = entity.children.map(m => m.code.name).join(', ')
      entity.children.forEach(c => {
        c['treatmentGroup'] = tgEntities
      })
      filteredTimeline = filteredTimeline.concat(entity.children)
      /* RULE #2: Group all GRAPH_TYPES (labs, performace status, vitals) with same code and only show most recent one */
    } else if (
      Constants.diseaseSummary.GRAPH_TYPES.includes(entity.entityType) &&
      !existsInSummary(entity, filteredTimeline)
    ) {
      let sameLabs = [],
        allLabs = []
      // go through original cc data and get all labs
      let justLabs = allCancerData.map(data => data[entity.entityType]).filter(t => t !== undefined)
      justLabs.map(lab => {
        sameLabs = lab
          .map(lab => ({ ...lab, entityType: entity.entityType }))
          .filter(m => m.id !== entity.id && m.code.code === entity.code.code)
        allLabs = allLabs.concat(sameLabs)
      })
      if (allLabs.length) {
        // add our current lab in as well
        allLabs = allLabs.concat(entity)
        orderByDate('desc', allLabs)
        // make the most recent entity the parent and add the rest as children
        let parent = allLabs.shift()
        parent.children = allLabs
        filteredTimeline.push(parent)
      } else {
        // just add our entity:
        filteredTimeline.push(entity)
      }
      /* RULE #3: For development entities, only show ones with the status of Able */
    } else if (
      Constants.diseaseSummary.DEVELOPMENT_ENTITIES.includes(entity.entityType) &&
      entity.valueUnits.code.name === 'Able'
    ) {
      filteredTimeline.push(entity)
      /* RULE #4: Neuro Only: Group all diagnostic procedures with same code and date */
    } else if (
      neuro &&
      entity.entityType === 'diagnosticProcedure' &&
      !existsInSummaryByCodeandDate(entity, filteredTimeline)
    ) {
      let allDpIds = []
      let entityDate = entity.eventDate.substring(0, entity.eventDate.indexOf('T'))
      let neuroDPs = neuro[entity.entityType] || []
      // go through neuro diagnostic procedures and grab ids of all the ones that meet criteria
      for (let dp of neuroDPs) {
        let dpDate = dp.eventDate.substring(0, dp.eventDate.indexOf('T'))
        if (entity.id !== dp.id && dpDate === entityDate && entity.code.name === dp.code.name) {
          allDpIds.push(dp.id)
        }
      }
      // now go through the graph and based on id, attach all children to "entity" diagnostic procedure
      for (let neuroGraph of neuro.graph) {
        if (
          neuroGraph.entityType === 'diagnosticProcedure' &&
          allDpIds.includes(neuroGraph.id) &&
          neuroGraph.children
        ) {
          entity.children = entity.children.concat(neuroGraph.children)
        }
      }
      filteredTimeline.push(entity)
      /* RULE #5: Do not show children of admission diagnosis in drill in method, only in subtext */
    } else if (entity.entityType === 'admissionDiagnosis') {
      // adding children to main entity to show in subtext
      if (entity.children) {
        entity.children.forEach(child => {
          entity[child.entityType] = child.code.name
        })
        // then removing children to prevent drilling in
        delete entity.children
      }
      filteredTimeline.push(entity)
      /* Push all other entities that shouldn't be filtered to the filtered timeline */
    } else if (
      !Constants.diseaseSummary.ENTITIES_TO_FILTER.includes(entity.entityType) &&
      !Constants.diseaseSummary.CATEGORIES_TO_FILTER.includes(entity.category)
    ) {
      filteredTimeline.push(entity)
    }
  }
  /* RULE #6: Show orphan biomarkers or md grouped by date. */
  let biomarkersAndMolecularDiagnostics = cancerGraph.filter(
    c => c.entityType === 'biomarker' || c.entityType === 'molecularDiagnostic'
  )
  let bioAndMDgroupedByDate = groupEntitiesByDate(biomarkersAndMolecularDiagnostics)
  filteredTimeline = filteredTimeline.concat(bioAndMDgroupedByDate)

  /* RULE #7: Neuro Only: Group together all entities whose category is 'physical-exam' and have the same date */
  let physicalExams = cancerGraph.filter(c => c.category === 'physical-exam')
  let physicalExamsOrdered = groupEntitiesByDate(physicalExams)
  for (let pe of physicalExamsOrdered) {
    let copy = lodashClonedeep(pe)
    delete copy.children
    if (!pe.children) pe.children = []
    pe.children.push(copy)
    pe.code.name = 'Physical Exam'
  }
  filteredTimeline = filteredTimeline.concat(physicalExamsOrdered)

  filteredTimeline = uniqBy(filteredTimeline, 'id')
  return filteredTimeline
}

export function existsInSummary(entity, summary) {
  let typeToCompare = entity.entityType === 'labResult' ? 'labResult' : 'performanceStatus'
  return summary
    .filter(m => m.key === typeToCompare)
    .map(m => m.code.code)
    .includes(entity.code.code)
}

/**
 * Sorts the resources by date
 * @param {Array} arr | An array of cancer resource objects
 * @param {String} order | Sorting order for resources
 */
export function orderByDate(order, arr) {
  if (!Array.isArray(arr)) return
  return arr.sort((a, b) => {
    let bDate = b.eventDate || b.started || b.ended || b.date || b
    let aDate = a.eventDate || a.started || a.ended || a.date || a
    if (order === 'desc') {
      return new Date(bDate) - new Date(aDate)
    } else if (order === 'asc') {
      return new Date(aDate) - new Date(bDate)
    }
  })
}

/**
 * Given the current cancer summary, group all orphan biomarkers by date. Attaches all same date orphans to one of them as "children"
 * @param  {Object} graph                     Current graph (contains relationships) of the current cancer summary
 * @param  {Array}  currentCancerSummary      An object of arrays of all the detailed entities in the current liquid or solid summary
 * @return {Array}  sortedOrphans             An array of molecular diagnostics or biomarkers sorted by date
 */
export function groupEntitiesByDate(graph) {
  let entities = [],
    sortedOrphans = []
  for (let g of graph) {
    let gDate = g.eventDate || g.started || g.ended
    let date = gDate.split('T')[0]
    g.eventDate = date
    entities.push(g)
  }
  let groupedByDate = groupBy(entities, 'eventDate')
  // iterate the object
  for (let index in groupedByDate) {
    let current = groupedByDate[index]
    if (current.length > 1) {
      // removes first elelment from current and assigns it to entity variable
      let entity = current.shift()
      entity.children = current
      sortedOrphans.push(entity)
    } else {
      let entity = current.shift()
      sortedOrphans.push(entity)
    }
  }
  return sortedOrphans
}

export function existsInSummaryByCodeandDate(entity, summary) {
  let date = entity.eventDate || entity.started
  let entityDate = date.substring(0, date.indexOf('T'))
  for (let sum of summary) {
    if (sum.entityType === 'diagnosticProcedure') {
      let sumD = sum.eventDate || sum.started
      let sumDate = sumD.substring(0, sumD.indexOf('T'))
      return sumDate === entityDate && sum.code.name === entity.code.name
    }
  }
}
